import React from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { FRONT_DOOR_FOOTER } from '../../constants/regulatoryFooter';
import { WAITLIST_COPY } from '../waitlist/waitlistCopy';

// dwlf.co.uk for someone who isn't signed in (DWLF-145). DWLF sells metals decision support today, so
// this is a front door, not a product page: the same umbrella line as metals.dwlf.co.uk, one route
// there, and sign-in for existing users. No pricing, no feature tour, no "signals"/"AI" (the metals
// messaging brief's never-say list). Signed-in users go straight to Today.

const METALS_URL = 'https://metals.dwlf.co.uk';
const tok = (t) => `var(--dwlf-${t})`;
const MONO = '"IBM Plex Mono", monospace';
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.16em' };

const linkSx = {
  ...LABEL, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 48, px: 3,
  fontSize: '0.75rem', textDecoration: 'none',
};

export default function FrontDoor() {
  const user = useSelector((s) => s.account.user);
  if (user) return <Navigate to="/today" replace />;
  return (
    <Box component="main" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: tok('bg'), color: tok('text') }}>
      <Box component="header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, md: 6 }, py: 3 }}>
        <Typography component="span" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.375rem', letterSpacing: '0.1em', color: tok('accent') }}>
          DWLF
        </Typography>
        <Box component={RouterLink} to="/login" sx={{ ...LABEL, fontSize: '0.6875rem', color: tok('ink-70'), textDecoration: 'none', py: 1.5, '&:hover': { color: tok('accent') } }}>
          Sign in
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: { xs: 2, md: 6 } }}>
        <Box sx={{ maxWidth: 880, display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
          <Typography component="p" sx={{ ...LABEL, fontSize: '0.75rem', color: tok('warn') }}>
            Rules-based decision support
          </Typography>
          <Typography component="h1" sx={{ m: 0, fontFamily: '"Space Grotesk", sans-serif', fontWeight: 500, fontSize: { xs: '2.5rem', md: '4.5rem' }, lineHeight: 1.02, letterSpacing: '-0.03em' }}>
            We don't predict the price.
            <Box component="span" sx={{ display: 'block', color: tok('accent') }}>We structure the decision.</Box>
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: { xs: '0.9375rem', md: '1.0625rem' }, lineHeight: 1.7, color: tok('ink-70'), maxWidth: 620 }}>
            DWLF Metals gives companies that hold precious metals the market context for their own decisions,
            with every decision on the record.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box component="a" href={METALS_URL} sx={{ ...linkSx, backgroundColor: tok('accent'), color: tok('on-accent'), '&:hover': { backgroundColor: tok('accent-hi') } }}>
              DWLF Metals →
            </Box>
            {/* Retail is invite-only (DWLF-584): a visitor from outside joins the waitlist. */}
            <Box component={RouterLink} to="/waitlist?from=landing" sx={{ ...linkSx, border: `1px solid ${tok('ink-20')}`, color: tok('text'), '&:hover': { borderColor: tok('accent'), color: tok('accent') } }}>
              {WAITLIST_COPY.cta}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box component="footer" sx={{ px: { xs: 2, md: 6 }, py: 3, borderTop: `1px solid ${tok('ink-08')}` }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: tok('ink-50') }}>
          {FRONT_DOOR_FOOTER}{' '}
          <Box component={RouterLink} to="/privacy" sx={{ color: tok('ink-50') }}>
            Privacy
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}
